import { Component, computed, effect, input, OnDestroy, OnInit, output, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MiqBehaviors } from '@miq/shared-models';
import { isMasked, simpleValueValidation } from '@miq/utils';
import { debounceTime } from 'rxjs/operators';
import { TextInput } from '../text-input';
import { MatFormField } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AvoidCopyPasteDirective } from '../../../directives/avoid-copy-paste/avoid-copy-paste';
import { MatIcon } from '@angular/material/icon';
import { ErrorStateMatcher } from '@angular/material/core';
import { L10nTranslatePipe } from 'angular-l10n';
import { ShowOnInvalidErrorStateMatcher } from '../../../utils/show-on-invalid-error-state-matcher';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-text',
  templateUrl: './text.component.html',
  styleUrl: './text.component.scss',
  providers: [
    { provide: MiqBehaviors, useExisting: TextComponent },
    { provide: ErrorStateMatcher, useClass: ShowOnInvalidErrorStateMatcher },
  ],
  imports: [MatFormField, NgClass, MatInput, FormsModule, ReactiveFormsModule, AvoidCopyPasteDirective, MatIcon, L10nTranslatePipe],
})
export class TextComponent extends TextInput implements OnInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);
  placeholder = input('');
  private _cvInitialized = false;
  readonly defaultEmptyValue = input(''); // TODO: Change to null when null values become a valid value on EDP
  autocomplete = input<boolean>();
  customConfigs = input<any>({});
  //@Input() column:any
  currentValueChange = output<string>()
  clear = output<string>()
  autocompleteValue = 'no-autofill';
  customSearchConfig: { isEnabled: boolean };
  readonly customClearButtonConfig = computed<{ isEnabled: boolean; restrictTyping?: boolean; currentValue?: string }>(
    () => (this.customConfigs() && this.customConfigs().clearButton) || { isEnabled: false }
  );
  readonly clearButtonEnabled = computed(
    () => this.customClearButtonConfig().isEnabled && !!this.customClearButtonConfig().currentValue
  );
  readonly customValue = computed(() =>
    this.clearButtonEnabled() ? this.customClearButtonConfig().currentValue : null
  );
  constructor() {
    super();
    effect(() => {
      this.currentValueInput(); // track
      if (!this._cvInitialized) { this._cvInitialized = true; return; }
      if (this.fc) this.fc.setValue(this.currentValue);
    });
  }
  ngOnInit(): void {
    super.ngOnInit();
    this.autocompleteValue = this.autocomplete() ? 'on' : 'no-autofill';
    this.customSearchConfig = this.customConfigs() && this.customConfigs().search;
    const isSearchInput = this.customSearchConfig && this.customSearchConfig.isEnabled;
    if (isSearchInput) {
      this.fc.valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.onChange(this.clearButtonEnabled() ? this.customClearButtonConfig().currentValue : null);
      });
    }
  }
  ngOnDestroy(): void {
    // required by untilDestroyed
  }
  public blurChange() {
    // prevent disabling by typing 5 stars
    if (isMasked(this.fc.value)) {
      this.fc.setValue(this.defaultEmptyValue());
    }
    this.onChange(this.clearButtonEnabled() ? this.customClearButtonConfig().currentValue : null);
  }
  public onChange(customValue?: string) {
    const newValue = customValue || this.fc.value;
    this.currentValue = simpleValueValidation(newValue) ? newValue : this.defaultEmptyValue();
    let value = this.currentValue;
    if (this.currentValue != null) {
      value = this.valueSetter ? this.valueSetter(this.currentValue) : this.currentValue.trim();
    }
    this.currentValueChange.emit(value);
  }
  public clearValue() {
    this.clear.emit(this.currentValue);
    this.fc.setValue(this.defaultEmptyValue());
  }
}
